import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound, Loader2, CheckCircle, AlertCircle, Eye, EyeOff,
  RefreshCw, Clock, AlertTriangle, XCircle, Settings2, PlugZap,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import { getErrorMessage } from '../../services/apiService';
import {
  listPortalCredentials,
  updatePortalCredential,
  updatePortalCredentialExpiry,
  checkPortalHealth,
  PortalCredential,
  PortalHealthResult,
  ExpiryStatus,
} from '../../services/portalCredentialsService';
import NcRotationPanel from './NcRotationPanel';

const ROTATION_OPTIONS = [30, 45, 60, 90, 180, 365];
const REMINDER_OPTIONS = [7, 14, 30];

// "Password last changed" is a calendar date saved as midnight UTC, and the reminder email counts
// from it in UTC. Shown in local time it reads as the day before anywhere west of Greenwich.
const formatUtcDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { timeZone: 'UTC' });

// Today's date as the admin sees it. toISOString() would give tomorrow's after 7pm Central.
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function ExpiryBadge({
  status, expiryDate, okLabel = 'Password OK',
}: { status: ExpiryStatus; expiryDate: string | null; okLabel?: string }) {
  if (status === 'None') return null;

  const label =
    status === 'OK' ? okLabel
    : status === 'ExpiringSoon' ? 'Expiring Soon'
    : status === 'Overdue' ? 'Overdue'
    : 'No date set';

  const expDate = expiryDate ? new Date(expiryDate) : null;
  const daysLeft = expDate
    ? Math.ceil((expDate.getTime() - Date.now()) / 86400000)
    : null;

  const [bg, border, text, Icon] =
    status === 'OK'
      ? ['rgba(34,197,94,0.1)', 'rgba(34,197,94,0.3)', 'text-green-700', CheckCircle]
    : status === 'ExpiringSoon'
      ? ['rgba(234,179,8,0.1)', 'rgba(234,179,8,0.3)', 'text-yellow-700', AlertTriangle]
    : status === 'Overdue'
      ? ['rgba(220,38,38,0.1)', 'rgba(220,38,38,0.3)', 'text-red-700', XCircle]
      : ['rgba(148,163,184,0.1)', 'rgba(148,163,184,0.3)', 'text-slate-500', Clock];

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${text}`}
      style={{ backgroundColor: bg, borderColor: border }}
      title={expiryDate ? `Expires ${formatUtcDate(expiryDate)}` : undefined}
    >
      <Icon className="w-3 h-3" />
      {label}
      {daysLeft !== null && status !== 'OK' && (
        <span className="ml-0.5">
          {daysLeft < 0 ? `(${Math.abs(daysLeft)}d ago)` : `(${daysLeft}d)`}
        </span>
      )}
    </span>
  );
}

const PortalCredentials: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const isSystemAdmin = user?.isSystemAdmin === true;

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (!isSystemAdmin) navigate('/dashboard');
  }, [user, authLoading, isSystemAdmin, navigate]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [credentials, setCredentials] = useState<PortalCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Password edit modal
  const [editState, setEditState] = useState<PortalCredential | null>(null);
  const [formUsername, setFormUsername] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Expiry config inline panel
  const [expiryOpen, setExpiryOpen] = useState<string | null>(null);
  const [expiryEnabled, setExpiryEnabled] = useState(false);
  const [expiryRotation, setExpiryRotation] = useState<number>(90);
  const [expiryReminder, setExpiryReminder] = useState<number>(14);
  const [expiryLastChanged, setExpiryLastChanged] = useState<string>('');
  const [expiryBusy, setExpiryBusy] = useState<string | null>(null);
  const [expiryError, setExpiryError] = useState('');

  // Inactivity-revoke config, part of the same inline panel
  // On-demand login check. Keyed by state code so several can run at once and each row
  // shows its own result rather than a single shared banner.
  const [healthBusy, setHealthBusy] = useState<string | null>(null);
  const [healthResults, setHealthResults] = useState<Record<string, PortalHealthResult | { ok: false; error: string }>>({});

  const [inactivityEnabled, setInactivityEnabled] = useState(false);
  const [inactivityRevokeDays, setInactivityRevokeDays] = useState<number>(45);
  const [inactivityReminder, setInactivityReminder] = useState<number>(14);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await listPortalCredentials();
      setCredentials(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSystemAdmin) load();
  }, [isSystemAdmin, load]);

  const runHealthCheck = async (cred: PortalCredential) => {
    setHealthBusy(cred.stateCode);
    setHealthResults((prev) => {
      const next = { ...prev };
      delete next[cred.stateCode];
      return next;
    });
    try {
      const result = await checkPortalHealth(cred.stateCode);
      setHealthResults((prev) => ({ ...prev, [cred.stateCode]: result }));
    } catch (err) {
      setHealthResults((prev) => ({
        ...prev,
        [cred.stateCode]: { ok: false, error: getErrorMessage(err) },
      }));
    } finally {
      setHealthBusy(null);
    }
  };

  const openEdit = (cred: PortalCredential) => {
    setEditState(cred);
    setFormUsername(cred.username ?? '');
    setFormPassword('');
    setShowPassword(false);
    setFormError('');
  };

  const closeEdit = () => {
    setEditState(null);
    setFormUsername('');
    setFormPassword('');
    setFormError('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editState) return;
    if (!formPassword.trim()) { setFormError('Password is required.'); return; }
    try {
      setSaving(true);
      setFormError('');
      await updatePortalCredential(editState.stateCode, formUsername.trim(), formPassword);
      setSuccess(`Credentials updated for ${editState.stateName} (${editState.stateCode}).`);
      closeEdit();
      load();
    } catch (err) {
      setFormError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleExpiryPanel = (cred: PortalCredential) => {
    if (expiryOpen === cred.stateCode) {
      setExpiryOpen(null);
    } else {
      setExpiryOpen(cred.stateCode);
      setExpiryEnabled(cred.passwordExpiryEnabled);
      setExpiryRotation(cred.passwordRotationDays ?? 90);
      setExpiryReminder(cred.passwordExpiryReminderDays ?? 14);
      setExpiryLastChanged(
        cred.passwordLastChangedAt
          ? new Date(cred.passwordLastChangedAt).toISOString().split('T')[0]
          : ''
      );
      setInactivityEnabled(cred.inactivityRevokeEnabled);
      setInactivityRevokeDays(cred.inactivityRevokeDays ?? 45);
      setInactivityReminder(cred.inactivityReminderDays ?? 14);
      setExpiryError('');
    }
  };

  const handleSaveExpiry = async (cred: PortalCredential) => {
    const stateCode = cred.stateCode;
    setExpiryBusy(stateCode);
    setExpiryError('');
    try {
      await updatePortalCredentialExpiry(stateCode, {
        passwordExpiryEnabled: expiryEnabled,
        passwordRotationDays: expiryEnabled ? expiryRotation : null,
        passwordExpiryReminderDays: expiryEnabled ? expiryReminder : null,
        passwordLastChangedAt: expiryLastChanged
          ? new Date(expiryLastChanged).toISOString()
          : null,
        // An API account has no inactivity setting; leaving these out leaves the stored values alone.
        ...(cred.isApiAccount ? {} : {
          inactivityRevokeEnabled: inactivityEnabled,
          inactivityRevokeDays: inactivityEnabled ? inactivityRevokeDays : null,
          inactivityReminderDays: inactivityEnabled ? inactivityReminder : null,
        }),
      });
      setSuccess(`Expiry settings saved for ${stateCode}.`);
      setExpiryOpen(null);
      load();
    } catch (err) {
      setExpiryError(getErrorMessage(err));
    } finally {
      setExpiryBusy(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-rose-500/15">
              <KeyRound className="w-7 h-7 text-[#c41e3a]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#0f172a]">State Credentials</h1>
              <p className="text-[#64748b] mt-1">Logins for state DMV portals and API accounts</p>
            </div>
          </div>
          <button
            onClick={load}
            className="p-2 rounded-xl glass-card-strong text-[#64748b] hover:text-[#0f172a] transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl border flex items-center space-x-3"
            style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', borderColor: 'rgba(220, 38, 38, 0.3)' }}>
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 rounded-xl border flex items-center space-x-3"
            style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', borderColor: 'rgba(34, 197, 94, 0.3)' }}>
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        <div className="glass-card">
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
            </div>
          ) : credentials.length === 0 ? (
            <div className="p-12 text-center text-[#64748b]">
              No states with a login to maintain.
            </div>
          ) : (
            <div className="divide-y divide-black/[0.06]">
              {credentials.map((cred) => (
                <div key={cred.stateCode}>
                  {/* Main row */}
                  <div className="flex items-center justify-between px-6 py-4 gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#0f172a]">
                          {cred.stateCode} — {cred.stateName}
                        </span>
                        {cred.isApiAccount && (
                          <span className="text-xs px-2 py-0.5 rounded-full border border-black/[0.08] bg-slate-100 text-[#475569] font-medium">
                            API account
                          </span>
                        )}
                        <ExpiryBadge status={cred.expiryStatus} expiryDate={cred.expiryDate} />
                        <ExpiryBadge status={cred.inactivityStatus} expiryDate={cred.inactivityRevokeDate} okLabel="Activity OK" />
                      </div>
                      <div className="text-sm text-[#64748b] mt-0.5">
                        {cred.isApiAccount
                          ? <span>Password is kept in AASConfig</span>
                          : cred.hasCredential
                          ? <>Username: <span className="font-medium text-[#0f172a]">{cred.username || '(set)'}</span></>
                          : <span className="text-amber-600 font-medium">No credentials set</span>}
                        {cred.passwordLastChangedAt && (
                          <span className="ml-3 text-[#94a3b8]">
                            Last changed: {formatUtcDate(cred.passwordLastChangedAt)}
                          </span>
                        )}
                        {cred.lastActivityAt && (
                          <span className="ml-3 text-[#94a3b8]">
                            Last used: {new Date(cred.lastActivityAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => toggleExpiryPanel(cred)}
                        className={`p-2 rounded-xl border transition-colors ${
                          expiryOpen === cred.stateCode
                            ? 'bg-slate-100 border-slate-300 text-[#0f172a]'
                            : 'border-black/[0.08] text-[#64748b] hover:text-[#0f172a] hover:bg-slate-50'
                        }`}
                        title="Password expiry settings"
                      >
                        <Settings2 className="w-4 h-4" />
                      </button>
                      {!cred.isApiAccount && (
                      <>
                      <button
                        onClick={() => runHealthCheck(cred)}
                        disabled={healthBusy === cred.stateCode}
                        className="px-3 py-2 rounded-xl text-sm font-medium border border-black/[0.08] text-[#64748b] hover:text-[#0f172a] hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                        title="Log in and reach the search form. Submits no search, so nothing is billed."
                      >
                        {healthBusy === cred.stateCode ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Checking
                          </>
                        ) : (
                          <>
                            <PlugZap className="w-4 h-4" />
                            Test Login
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => openEdit(cred)}
                        className="px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-[#AD0000] to-red-700 text-white hover:from-red-700 hover:to-red-800 transition-all shadow-sm"
                      >
                        Update Password
                      </button>
                      </>
                      )}
                    </div>
                  </div>

                  {/* Login check result */}
                  {healthResults[cred.stateCode] && (
                    <div className="px-6 pb-4">
                      {healthResults[cred.stateCode].ok ? (
                        <div className="p-3 rounded-xl border flex items-start gap-2 text-sm text-green-800"
                          style={{ backgroundColor: 'rgba(22,163,74,0.08)', borderColor: 'rgba(22,163,74,0.25)' }}>
                          <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-medium">
                              Logged in and reached the search form
                              {'durationMs' in healthResults[cred.stateCode] &&
                                ` in ${((healthResults[cred.stateCode] as PortalHealthResult).durationMs / 1000).toFixed(1)}s`}
                              .
                            </span>
                            {/* Said every time, deliberately. A green tick here has burned us twice:
                                Washington and Florida both reported healthy while returning nothing. */}
                            <span className="block text-xs text-green-900/70 mt-0.5">
                              This proves the portal can be reached, not that a lookup parses correctly.
                              No search was submitted, so nothing was billed.
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 rounded-xl border flex items-start gap-2 text-sm text-red-800"
                          style={{ backgroundColor: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.25)' }}>
                          <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-medium">Login check failed.</span>
                            <span className="block text-xs mt-0.5 break-words">
                              {healthResults[cred.stateCode].error || 'No error message was returned.'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {cred.stateCode === 'NC' && <NcRotationPanel onChanged={load} />}

                  {/* Expiry settings panel */}
                  {expiryOpen === cred.stateCode && (
                    <div className="px-6 pb-5 bg-slate-50/60 border-t border-black/[0.04]">
                      <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wide pt-4 mb-3">
                        Password Expiry Settings
                      </p>
                      {cred.isApiAccount && (
                        <p className="text-xs text-[#94a3b8] mb-3">
                          This password is kept in AASConfig. After you change it there, press "Set to today"
                          so the countdown restarts.
                        </p>
                      )}

                      {expiryError && (
                        <div className="mb-3 p-3 rounded-xl border flex items-center gap-2 text-sm text-red-800"
                          style={{ backgroundColor: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.25)' }}>
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          {expiryError}
                        </div>
                      )}

                      <div className="flex flex-wrap items-end gap-4">
                        {/* Toggle */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => setExpiryEnabled(!expiryEnabled)}
                            className={`relative w-10 h-5 rounded-full transition-colors ${expiryEnabled ? 'bg-[#c41e3a]' : 'bg-slate-300'}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${expiryEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                          <span className="text-sm font-medium text-[#0f172a] whitespace-nowrap">Track expiry</span>
                        </div>

                        {/* Password last changed date — always shown so admins can record existing passwords */}
                        <div>
                          <label className="block text-xs text-[#64748b] mb-1">Password last changed</label>
                          <input
                            type="date"
                            value={expiryLastChanged}
                            max={localToday()}
                            onChange={(e) => setExpiryLastChanged(e.target.value)}
                            className="px-3 py-1.5 text-sm rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                          {/* Portal logins need it too: a password changed in AASConfig instead of
                              through Update Password is not stamped. */}
                          <button
                            type="button"
                            onClick={() => setExpiryLastChanged(localToday())}
                            className="ml-2 px-3 py-1.5 text-sm rounded-lg border border-black/[0.08] text-[#64748b] hover:text-[#0f172a] hover:bg-slate-50"
                          >
                            Set to today
                          </button>
                        </div>

                        {expiryEnabled && (
                          <>
                            {/* Rotation interval */}
                            <div>
                              <label className="block text-xs text-[#64748b] mb-1">Rotate every</label>
                              <select
                                value={expiryRotation}
                                onChange={(e) => setExpiryRotation(Number(e.target.value))}
                                className="px-3 py-1.5 text-sm rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                              >
                                {ROTATION_OPTIONS.map((d) => (
                                  <option key={d} value={d}>{d} days</option>
                                ))}
                              </select>
                            </div>

                            {/* Reminder window */}
                            <div>
                              <label className="block text-xs text-[#64748b] mb-1">Warn before expiry</label>
                              <select
                                value={expiryReminder}
                                onChange={(e) => setExpiryReminder(Number(e.target.value))}
                                className="px-3 py-1.5 text-sm rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                              >
                                {REMINDER_OPTIONS.map((d) => (
                                  <option key={d} value={d}>{d} days</option>
                                ))}
                              </select>
                            </div>
                          </>
                        )}
                      </div>

                      {!cred.isApiAccount ? (
                      <>
                      <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wide pt-4 mb-3">
                        Inactivity Revoke Settings
                      </p>
                      <p className="text-xs text-[#94a3b8] mb-3">
                        Some states auto-revoke the account after a period of no lookups (e.g. NC RACF, 45 days).
                        "Last used" is read from actual lookup history — no date to set manually.
                      </p>
                      <div className="flex flex-wrap items-end gap-4">
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => setInactivityEnabled(!inactivityEnabled)}
                            className={`relative w-10 h-5 rounded-full transition-colors ${inactivityEnabled ? 'bg-[#c41e3a]' : 'bg-slate-300'}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${inactivityEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                          <span className="text-sm font-medium text-[#0f172a] whitespace-nowrap">Track inactivity</span>
                        </div>

                        {inactivityEnabled && (
                          <>
                            <div>
                              <label className="block text-xs text-[#64748b] mb-1">Revoke after</label>
                              <select
                                value={inactivityRevokeDays}
                                onChange={(e) => setInactivityRevokeDays(Number(e.target.value))}
                                className="px-3 py-1.5 text-sm rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                              >
                                {ROTATION_OPTIONS.map((d) => (
                                  <option key={d} value={d}>{d} days</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-xs text-[#64748b] mb-1">Warn before revoke</label>
                              <select
                                value={inactivityReminder}
                                onChange={(e) => setInactivityReminder(Number(e.target.value))}
                                className="px-3 py-1.5 text-sm rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                              >
                                {REMINDER_OPTIONS.map((d) => (
                                  <option key={d} value={d}>{d} days</option>
                                ))}
                              </select>
                            </div>
                          </>
                        )}

                        <button
                          onClick={() => handleSaveExpiry(cred)}
                          disabled={expiryBusy === cred.stateCode}
                          className="px-4 py-1.5 rounded-lg text-sm font-medium bg-[#0f172a] text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
                        >
                          {expiryBusy === cred.stateCode
                            ? <Loader2 className="w-4 h-4 animate-spin inline" />
                            : 'Save'}
                        </button>
                      </div>
                      </>
                      ) : (
                      <div className="pt-4">
                        <button
                          onClick={() => handleSaveExpiry(cred)}
                          disabled={expiryBusy === cred.stateCode}
                          className="px-4 py-1.5 rounded-lg text-sm font-medium bg-[#0f172a] text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
                        >
                          {expiryBusy === cred.stateCode
                            ? <Loader2 className="w-4 h-4 animate-spin inline" />
                            : 'Save'}
                        </button>
                      </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </main>

      <Footer />

      {/* Password Edit Modal */}
      {editState && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full p-6 border border-black/[0.08] shadow-2xl">
            <h3 className="text-xl font-bold mb-1 text-[#0f172a]">Update Credentials</h3>
            <p className="text-sm text-[#64748b] mb-6">
              {editState.stateName} ({editState.stateCode})
            </p>

            {formError && (
              <div className="mb-4 p-3 rounded-xl border flex items-start space-x-3"
                style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', borderColor: 'rgba(220, 38, 38, 0.3)' }}>
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{formError}</p>
              </div>
            )}

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#0f172a]">Username</label>
                <input
                  type="text"
                  value={formUsername}
                  onChange={(e) => setFormUsername(e.target.value)}
                  placeholder="Leave blank to keep existing"
                  autoComplete="off"
                  className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#0f172a]">
                  New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    required
                    className="w-full px-4 py-2 pr-10 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="flex-1 px-4 py-2 rounded-xl border border-black/[0.08] text-[#64748b] hover:text-[#0f172a] font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-[#AD0000] to-red-700 text-white font-medium hover:from-red-700 hover:to-red-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Saving...</> : 'Save Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalCredentials;
